import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { useState, type ReactNode } from 'react';
import { MdClose, MdOutlineInsertDriveFile, MdPictureAsPdf, MdPlayCircleOutline } from 'react-icons/md';

import type { PostAttachmentItem } from './post-attachment-upload';
import { VideoPreviewDialog } from './video-preview-dialog';

interface AttachmentPreviewsProps {
  items: PostAttachmentItem[];
  onRemove: (id: string) => void;
}

/**
 * Thumbnail grid for picked / existing post attachments. Images and videos show
 * a preview, files show a labelled icon; each tile has a remove button and a
 * loading overlay while uploading. Clicking a video tile opens a player so the
 * author can preview it. Shared by the composer and the edit form.
 */
export const AttachmentPreviews = ({ items, onRemove }: AttachmentPreviewsProps) => {
  // Source of the video currently open in the player lightbox (null = closed).
  const [playingSrc, setPlayingSrc] = useState<string | null>(null);

  if (items.length === 0) {
    return null;
  }

  return (
    <>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {items.map(item => {
          let media: ReactNode;
          if (item.type === 'image') {
            media = (
              <Box
                component="img"
                src={item.preview}
                alt={item.name || 'Preview'}
                sx={{ width: '100%', height: '100%', borderRadius: '8px', objectFit: 'cover' }}
              />
            );
          } else if (item.type === 'video') {
            media = (
              <>
                <Box
                  component="video"
                  src={item.preview}
                  muted
                  onClick={() => setPlayingSrc(item.preview)}
                  sx={{
                    width: '100%',
                    height: '100%',
                    borderRadius: '8px',
                    objectFit: 'cover',
                    cursor: 'pointer',
                  }}
                />
                <Box
                  component={MdPlayCircleOutline}
                  onClick={() => setPlayingSrc(item.preview)}
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    color: 'white',
                    fontSize: 28,
                    cursor: 'pointer',
                  }}
                />
              </>
            );
          } else {
            const isPdf = item.mimeType === 'application/pdf' || (item.name?.toLowerCase().endsWith('.pdf') ?? false);
            media = (
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  borderRadius: '8px',
                  border: '1px solid #e0e0e0',
                  bgcolor: '#f7f7f7',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: '4px',
                  gap: '2px',
                }}
              >
                <Box
                  component={isPdf ? MdPictureAsPdf : MdOutlineInsertDriveFile}
                  sx={{ fontSize: 26, color: isPdf ? '#d32f2f' : '#888' }}
                />
                <Typography
                  sx={{
                    fontSize: 8,
                    lineHeight: '10px',
                    textAlign: 'center',
                    color: '#666',
                    width: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {item.name || 'File'}
                </Typography>
              </Box>
            );
          }

          return (
            <Box key={item.id} sx={{ position: 'relative', width: 80, height: 80, opacity: item.uploading ? 0.5 : 1 }}>
              {media}
              {item.uploading && (
                <CircularProgress
                  size={24}
                  sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}
                />
              )}
              {!item.uploading && (
                <IconButton
                  size="small"
                  onClick={() => onRemove(item.id)}
                  sx={{
                    position: 'absolute',
                    top: -8,
                    right: -8,
                    bgcolor: 'white',
                    boxShadow: 1,
                    '&:hover': { bgcolor: 'grey.100' },
                  }}
                >
                  <Box component={MdClose} sx={{ fontSize: 14 }} />
                </IconButton>
              )}
            </Box>
          );
        })}
      </Box>
      <VideoPreviewDialog src={playingSrc} onClose={() => setPlayingSrc(null)} />
    </>
  );
};
